/**
 * Approve a prepared quotation for the order system, withdraw that approval, and
 * approve the price of a line that needs an approval.
 *
 * An approval is a rep's intent, stored in the app database with an idempotency key.
 * Creating quotations in the order system is switched off: nothing reads the intent,
 * nothing is sent, and the answer always says `write_enabled: false`.
 *
 * Every write carries `expected_version`, the version of the case's work row the page
 * was showing (0 without one); a 409 answers with the current row, like the work routes.
 *
 * Kept free of React so the request shapes and the error wording can be tested.
 */

import type { QueryClient } from "@tanstack/react-query";

import { type ApiError, client } from "./client";
import type {
  CaseWork,
  FoCheck,
  LinePriceApproval,
  LinePriceApprovalRequest,
  QuotationApproval,
  QuotationApprovalHistory,
  QuotationApprovalRequest,
  QuotationWithdrawRequest,
} from "./live-types";
import { caseWriteKey, detailText, expectedVersion, workFailure } from "./work-actions";

/** Shown with every approval, whatever the server says. */
export const APPROVAL_SWITCHED_OFF =
  "Creating quotations in the order system is switched off. Nothing was sent.";

/** The query keys this module reads and invalidates. */
export const approvalKey = (caseId: string) => ["case-quotation-approval", caseId];

/** The approval of a case and its history; an older backend (404) has none. */
export async function getQuotationApproval(caseId: string): Promise<QuotationApprovalHistory> {
  try {
    const { data } = await client.get<{ 200: QuotationApprovalHistory }, unknown, true>({
      url: "/cases/{case_id}/quotation-approval",
      path: { case_id: caseId },
      throwOnError: true,
    });
    return {
      current: data?.current && typeof data.current === "object" ? data.current : null,
      history: Array.isArray(data?.history) ? data.history : [],
    };
  } catch (error) {
    if ((error as ApiError | null)?.status === 404) {
      return { current: null, history: [] };
    }
    throw error;
  }
}

/** Approve the prepared quotation (201 new, 200 when it was approved already). */
export async function postQuotationApproval(caseId: string, body: QuotationApprovalRequest) {
  const { data } = await client.post<
    { 200: QuotationApproval; 201: QuotationApproval },
    unknown,
    true
  >({
    url: "/cases/{case_id}/quotation-approval",
    path: { case_id: caseId },
    body,
    headers: { "Content-Type": "application/json" },
    throwOnError: true,
  });
  return data;
}

/** Withdraw the current approval; an empty note is not sent. */
export async function postQuotationWithdraw(caseId: string, body: QuotationWithdrawRequest) {
  const note = body.note?.trim();
  const { data } = await client.post<{ 200: QuotationApproval }, unknown, true>({
    url: "/cases/{case_id}/quotation-approval/withdraw",
    path: { case_id: caseId },
    body: note
      ? { expected_version: body.expected_version, note }
      : { expected_version: body.expected_version },
    headers: { "Content-Type": "application/json" },
    throwOnError: true,
  });
  return data;
}

/** Approve (or take back the approval of) the price of one line. No figure is sent or returned. */
export async function postLinePriceApproval(
  caseId: string,
  lineIndex: number,
  body: LinePriceApprovalRequest,
) {
  const { data } = await client.post<{ 200: LinePriceApproval }, unknown, true>({
    url: "/cases/{case_id}/lines/{line_index}/price-approval",
    path: { case_id: caseId, line_index: lineIndex },
    body,
    headers: { "Content-Type": "application/json" },
    throwOnError: true,
  });
  return data;
}

// ─── wording ────────────────────────────────────────────────────────────────

/** What a failed approval write means for the page. */
export type ApprovalFailure =
  | { kind: "conflict"; message: string; work: CaseWork | null }
  /** The preview is not ready: the checks that are still open. */
  | { kind: "blocked"; message: string; checks: FoCheck[] }
  | { kind: "forbidden"; message: string }
  | { kind: "not_found"; message: string }
  | { kind: "failed"; message: string };

function isCheck(raw: unknown): raw is FoCheck {
  return (
    raw != null &&
    typeof raw === "object" &&
    typeof (raw as FoCheck).label === "string" &&
    (raw as FoCheck).label.trim() !== ""
  );
}

/**
 * The checks a 422 names. The backend sends `{detail: {code, blocked: [...]}}`; the
 * older forms `{detail: {checks: [...]}}`, `{detail: [...]}` with check entries, and
 * `{checks: [...]}` are still read. Only the open (not ok) ones are kept.
 */
export function blockedChecksOf(body: unknown): FoCheck[] {
  const detail = (body as { detail?: unknown } | null)?.detail;
  const candidates: unknown[] = [];
  for (const source of [
    (detail as { blocked?: unknown } | null)?.blocked,
    (detail as { checks?: unknown } | null)?.checks,
    detail,
    (body as { checks?: unknown } | null)?.checks,
  ]) {
    if (Array.isArray(source)) {
      candidates.push(...source);
    }
  }
  return candidates
    .filter(isCheck)
    .filter((check) => check.status !== "ok" && check.status !== "not_applicable");
}

/** Read a thrown `ApiError` from an approval route. */
export function approvalFailure(error: unknown): ApprovalFailure {
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  if (status === 409) {
    const conflict = workFailure(error, "case");
    return {
      kind: "conflict",
      message: conflict.message,
      work: conflict.kind === "conflict" ? conflict.work : null,
    };
  }
  if (status === 422) {
    const checks = blockedChecksOf(body);
    const sentence = detailText(body);
    return {
      kind: "blocked",
      checks,
      message:
        checks.length > 0
          ? "Not approved. These checks are still open:"
          : (sentence ?? "Not approved. The quotation is not ready yet."),
    };
  }
  if (status === 403) {
    return {
      kind: "forbidden",
      message: "Only the person who approved it or a manager of the area can do this.",
    };
  }
  if (status === 404) {
    return { kind: "not_found", message: "Nothing is approved for this case." };
  }
  return { kind: "failed", message: "This could not be saved. Try again in a moment." };
}

/** True when this user may withdraw the approval: the approver, or a manager who may reopen. */
export function mayWithdraw(
  approval: Pick<QuotationApproval, "approved_by" | "state"> | null | undefined,
  email: string | null | undefined,
  mayReopenOthers: boolean,
): boolean {
  if (!approval || approval.state !== "approved") {
    return false;
  }
  if (mayReopenOthers) {
    return true;
  }
  const me = (email ?? "").trim().toLowerCase();
  return me !== "" && (approval.approved_by ?? "").trim().toLowerCase() === me;
}

/** The newest first, without the current approval while it stands (it is shown on its own). */
export function approvalHistory(
  state: QuotationApprovalHistory | null | undefined,
): QuotationApproval[] {
  const current = state?.current?.state === "approved" ? state.current.intent_id : undefined;
  return (state?.history ?? [])
    .filter((entry) => entry && entry.intent_id !== current)
    .sort((a, b) => Date.parse(b.approved_at ?? "") - Date.parse(a.approved_at ?? "") || 0);
}

// ─── mutations ──────────────────────────────────────────────────────────────

/** The slice of a cached case the approval writes read. */
interface CachedCase {
  work?: CaseWork;
}

export interface ApprovalMutationConfig<Args, Result> {
  queryClient: QueryClient;
  caseId: string;
  send: (args: Args, version: number) => Promise<Result | undefined>;
  notify: (message: string) => void;
}

/**
 * Shared behaviour of the approval writes, as TanStack mutation options:
 *   - `expected_version` is the version of the work row in the cached case (0 without);
 *   - on success the work row of the answer (a price approval carries it) goes into the
 *     cached case at once, so the next write sends the new version and never reads as
 *     "changed by a colleague"; then the approval, the history of the case, the case, the
 *     quotation preview and the queue are read again (ease round, 13 Oct 2026: the
 *     preview kept the old check and the queue kept the hold);
 *   - on 409 the conflict toast is raised, the current work row is cached and the case
 *     is read again; 422, 403 and 404 stay on `mutation.error` for the panel.
 */
export function approvalMutationOptions<Args, Result>(
  config: ApprovalMutationConfig<Args, Result>,
) {
  const { queryClient, caseId, send, notify } = config;
  const caseKey = ["case", caseId];
  return {
    // One after the other with every other write of the case: each sends the version
    // the answer before brought.
    scope: { id: caseWriteKey(caseId).join(":") },
    mutationFn: async (args: Args) => {
      const shown = queryClient.getQueryData<CachedCase>(caseKey);
      return send(args, expectedVersion(shown?.work));
    },
    onSuccess: (result?: Result) => {
      const work = (result as { work?: CaseWork | null } | null | undefined)?.work;
      if (work && typeof work === "object") {
        queryClient.setQueryData<CachedCase>(caseKey, (detail) =>
          detail ? { ...detail, work } : detail,
        );
      }
      void queryClient.invalidateQueries({ queryKey: approvalKey(caseId) });
      void queryClient.invalidateQueries({ queryKey: ["case-events", caseId] });
      void queryClient.invalidateQueries({ queryKey: caseKey });
      void queryClient.invalidateQueries({ queryKey: ["case-fo-draft", caseId] });
      void queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
    onError: (error: unknown) => {
      const failure = approvalFailure(error);
      if (failure.kind !== "conflict") {
        return;
      }
      notify(failure.message);
      if (failure.work) {
        const work = failure.work;
        queryClient.setQueryData<CachedCase>(caseKey, (detail) =>
          detail ? { ...detail, work } : detail,
        );
      }
      void queryClient.invalidateQueries({ queryKey: caseKey });
    },
  };
}
